/* istanbul ignore file */
/**
 * Created by gboue on 8/29/22
 * File: VerificationInstructions
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, EventHandler, useContext, useCallback } from 'react'
import { Body, Bold, Button, ModalFooter, Title, Banner, Input, useModal } from '@panwds/react-ui'
import {
  BUTTON_FINISH,
  BUTTON_PREVIOUS,
  STEP_VERIFY_INSTRUCTIONS,
  INSTRUCTIONS_SUBTEXT_1,
  INSTRUCTIONS_SUBTEXT_2,
  LABEL_INSTRUCTIONS_1,
  INSTRUCTIONS_SUBTEXT_3,
  LABEL_INSTRUCTIONS_2,
  INSTRUCTIONS_SUBTEXT_4,
  INSTRUCTIONS_SUBTEXT_5,
} from '../../../constants/LabelConstants'
import { Form } from '@panwds/react-form'
import { DATA_TEST_DOMAIN_FINISH_BUTTON, DATA_TEST_DOMAIN_PREVIOUS_BUTTON } from '../../../../common/constants/TestingDataIds'
import DomainModal from '../modal/DomainModal'
import ScreenLoader from '../../../../common/components/screenLoader/ScreenLoader'
import GlobalContext from '../../../../common/context/GlobalContext'

const Footer: VFC<{
  loading: boolean,
  onBackPressed: EventHandler<any>,
  closeModal: () => void
}> = ({ loading, onBackPressed, closeModal }) => {
  return (
    <ModalFooter addClassName='tw-absolute' style={{ right: 0, left: 0, bottom: 0, height: '43px' }}>
      <Button
        onClick={onBackPressed}
        appearance='secondary'
        disabled={loading}
        dataTestId={DATA_TEST_DOMAIN_PREVIOUS_BUTTON}
      >{BUTTON_PREVIOUS}</Button>
      <Button
        appearance='primary'
        disabled={loading}
        dataTestId={DATA_TEST_DOMAIN_FINISH_BUTTON}
        onClick={closeModal}
      >
        {BUTTON_FINISH}
      </Button>
    </ModalFooter>
  )
}

const VerificationInstructions: VFC<{
  onBackPressed: EventHandler<any>,
  loading: boolean,
  domain: string,
  fetchData: Function,
  verificationString: string,
  shouldFetch?: boolean,
}> = ({ onBackPressed, loading, domain, fetchData, verificationString, shouldFetch = true }) => {
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { closeModal: _closeModal } = useModal(DomainModal)

  const closeModal = useCallback(() => {
    const tsg_id = selectedTreeNode?.id
    _closeModal()
    shouldFetch && fetchData({ tsg_id, loading: true })
  }, [_closeModal, fetchData, shouldFetch, selectedTreeNode?.id])

  return (
    <Form formProps={{ style: { height: '100%', width: '100%', position: 'relative' } }}>
      <div className='tw-relative tw-overflow-y-scroll' style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: '43px' }}>
        <div className='tw-flex tw-flex-col tw-gap-4 tw-p-4' style={{ width: '100%' }}>
          <Title level={1} size='lg' addClassName='tw-font-semibold'>{STEP_VERIFY_INSTRUCTIONS}</Title>
          <Body>{INSTRUCTIONS_SUBTEXT_1}</Body>
          <Body>{INSTRUCTIONS_SUBTEXT_2}</Body>
          <Bold appearance='semibold'>{LABEL_INSTRUCTIONS_1}</Bold>
          <Body>{INSTRUCTIONS_SUBTEXT_3}</Body>
          <div className='tw-flex tw-items-center'>
            { loading ? <ScreenLoader/> : (
              <Input
                allowCopy
                readOnly
                addClassName="tw-truncate tw-py-6 tw-flex-1 tw-bg-gray-100 dark:tw-bg-gray-1050"
                name="txt_record"
                value={verificationString}
              />)}
          </div>
          <Bold appearance='semibold'>{LABEL_INSTRUCTIONS_2}</Bold>
          <Body>{INSTRUCTIONS_SUBTEXT_4}</Body>
          <Banner type='inline' appearance='standard' showIcon>
            {INSTRUCTIONS_SUBTEXT_5}
          </Banner>
        </div>
      </div>
      <Footer {...{ loading, onBackPressed, domain, closeModal, fetchData }}/>
    </Form>
  )
}

export default VerificationInstructions

